export type ApiSuccessEnvelope<T> = {
  success: true;
  result: T;
};

export type ApiErrorPayload = {
  success?: false;
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
};

export class ApiClientError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;
  payload?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
    payload?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.payload = payload;
  }
}

export type JsonRequestInit = Omit<RequestInit, "body"> & {
  apiBaseUrl?: string;
  body?: BodyInit | null;
  bodyJson?: Record<string, unknown>;
};

type DirectMockApiResolver = (
  requestUrl: string,
  init: RequestInit,
) => Promise<{
  status: number;
  body: unknown;
}>;

let directMockApiResolver: DirectMockApiResolver | null = null;

export function setDirectMockApiResolver(
  resolver: DirectMockApiResolver | null,
): DirectMockApiResolver | null {
  const previousResolver = directMockApiResolver;
  directMockApiResolver = resolver;
  return previousResolver;
}

export function buildApiUrl(apiBaseUrl: string | undefined, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://") || !apiBaseUrl) {
    return path;
  }

  return `${apiBaseUrl.replace(/\/$/, "")}${path}`;
}

export function parseEnvelope<T>(payload: ApiSuccessEnvelope<T> | ApiErrorPayload): T {
  if ("success" in payload && payload.success === true) {
    return payload.result;
  }

  throw new ApiClientError(
    500,
    payload.error?.code || "INVALID_ENVELOPE",
    payload.error?.message || "Expected a success envelope",
    payload.error?.details,
    payload,
  );
}

function buildBody(init?: JsonRequestInit): BodyInit | undefined {
  if (!init?.bodyJson) {
    return undefined;
  }

  return JSON.stringify(init.bodyJson);
}

function buildHeaders(init?: JsonRequestInit): Headers {
  const headers = new Headers(init?.headers || {});
  if (init?.bodyJson) {
    headers.set("content-type", "application/json");
  }
  return headers;
}

function getErrorMessage(status: number, payload: unknown): string {
  if (payload && typeof payload === "object" && "error" in payload) {
    const message = (payload as ApiErrorPayload).error?.message;
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }

  return `Request failed with status ${status}`;
}

function getErrorCode(payload: unknown): string {
  if (payload && typeof payload === "object" && "error" in payload) {
    const code = (payload as ApiErrorPayload).error?.code;
    if (typeof code === "string" && code.length > 0) {
      return code;
    }
  }

  return "UNKNOWN_ERROR";
}

function getErrorDetails(payload: unknown): Record<string, unknown> | undefined {
  if (payload && typeof payload === "object" && "error" in payload) {
    return (payload as ApiErrorPayload).error?.details;
  }

  return undefined;
}

function parseJsonText(text: string, status: number): unknown {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiClientError(status, "INVALID_JSON", "Response was not valid JSON");
  }
}

function shouldUseDirectMockApi(apiBaseUrl: string | undefined): boolean {
  if (!apiBaseUrl) {
    return false;
  }

  try {
    return new URL(apiBaseUrl).hostname === "fluentry.test";
  } catch {
    return false;
  }
}

// Keep all browser-facing JSON calls on one code path so auth/admin/public surfaces interpret envelopes and failures consistently.
export async function requestJson<T>(path: string, init?: JsonRequestInit): Promise<T> {
  const body = buildBody(init);
  const headers = buildHeaders(init);
  const {
    apiBaseUrl,
    body: _rawBody,
    bodyJson: _bodyJson,
    credentials,
    headers: _ignoredHeaders,
    method,
    ...requestInit
  } = init ?? {};

  if (shouldUseDirectMockApi(apiBaseUrl)) {
    if (!directMockApiResolver) {
      throw new ApiClientError(
        500,
        "MOCK_API_NOT_CONFIGURED",
        "fluentry.test mock API requests require an explicit mock resolver",
      );
    }

    // Playwright CT cannot always rely on service-worker interception. The mock resolver is injected by preview/test surfaces so production bundles never import mock API code.
    const response = await directMockApiResolver(buildApiUrl(apiBaseUrl, path), {
      ...requestInit,
      method: method || "GET",
      ...(headers.keys().next().done ? {} : { headers }),
      ...(body === undefined ? {} : { body }),
    });
    if (response.status < 200 || response.status >= 300) {
      throw new ApiClientError(
        response.status,
        getErrorCode(response.body),
        getErrorMessage(response.status, response.body),
        getErrorDetails(response.body),
        response.body,
      );
    }
    return response.body as T;
  }

  const response = await fetch(buildApiUrl(apiBaseUrl, path), {
    // Preserve caller-supplied fetch controls so Next cache/revalidate semantics actually reach the runtime.
    ...requestInit,
    method: method || "GET",
    credentials: credentials || "include",
    ...(headers.keys().next().done ? {} : { headers: Object.fromEntries(headers.entries()) }),
    ...(body === undefined ? {} : { body }),
  });

  const payload =
    typeof response.text === "function"
      ? parseJsonText(await response.text(), response.status)
      : typeof response.json === "function"
        ? ((await response.json()) as unknown)
        : null;

  if (!response.ok) {
    throw new ApiClientError(
      response.status,
      getErrorCode(payload),
      getErrorMessage(response.status, payload),
      getErrorDetails(payload),
      payload,
    );
  }

  return payload as T;
}
